import { useEffect, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { Icon, type IconName } from '../components/primitives'
import { SearchOverlay } from '../components/Search'
import { ThemeButton } from '../components/ThemeToggle'
import { SiteFooter } from '../components/Disclosures'
import { useTitle } from '../title'

// PodcastAlpha Mobile.html: a sticky top bar (brand on tab roots; back button
// and, once scrolled, the page title on detail pages) and a fixed tab bar.
// The design's in-memory screen stack is real URLs and browser history here.

interface Tab {
  to: string
  label: string
  icon: IconName
  owns: (path: string) => boolean
}

const TABS: Tab[] = [
  { to: '/', label: 'Picks', icon: 'rec', owns: (p) => p === '/' || p.startsWith('/rec/') },
  { to: '/stocks', label: 'Stocks', icon: 'stock', owns: (p) => p.startsWith('/stocks') },
  { to: '/podcasts', label: 'Podcasts', icon: 'podcast', owns: (p) => p.startsWith('/podcasts') },
  { to: '/episodes', label: 'Episodes', icon: 'episode', owns: (p) => p.startsWith('/episodes') },
]

export function MobileShell({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const title = useTitle()
  const [scrolled, setScrolled] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)

  const path = location.pathname
  const isRoot = TABS.some((t) => t.to === path)
  // A page outside every tab (e.g. /methodology) highlights none; back still
  // has the first tab to fall back to.
  const current = TABS.find((t) => t.owns(path))
  const active = current ?? TABS[0]

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [path])

  // A page opened from a shared link has no in-app history to go back to; back
  // then goes to the page's tab.
  const back = () => {
    if (location.key !== 'default') navigate(-1)
    else navigate(active.to, { replace: true })
  }

  return (
    <div className="m" data-layout="mobile">
      <header className={`m-top ${scrolled ? 'scrolled' : ''}`}>
        {isRoot ? (
          <Link to="/" className="brand grow"><span className="dot" />PodcastAlpha</Link>
        ) : (
          <>
            <button className="back" onClick={back} aria-label="Back"><Icon name="chevron" size={22} /></button>
            <span className="ttl">{scrolled ? title : ''}</span>
          </>
        )}
        <ThemeButton />
        <button className="icbtn" aria-label="Search" onClick={() => setSearchOpen(true)}>
          <Icon name="search" size={20} />
        </button>
      </header>
      <main className={`m-scroll ${path.startsWith('/rec/') ? 'with-sticky' : ''}`}>
        {children}
        <SiteFooter />
      </main>
      <nav className="m-tabs" aria-label="Sections">
        {TABS.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            className={`m-tab ${current === t ? 'active' : ''}`}
            aria-current={current === t ? 'page' : undefined}
            onClick={() => {
              // Tapping the current tab's root scrolls back to the top.
              if (path === t.to) window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
          >
            <span className="tab-ic"><Icon name={t.icon} size={22} /></span>
            {t.label}
          </Link>
        ))}
      </nav>
      {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} />}
    </div>
  )
}
